import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import useStore from '../../store/useStore';
import { useDashboardDateRange } from './wallSection';
import { statementsAPI } from '../../services/api';
import StatementView from './StatementView';
import FreshnessBadge from './FreshnessBadge';
import WidgetPeriodPicker from './WidgetPeriodPicker';
import '../../styles/Statements.css';

const DEFAULT_REFRESH_MINUTES = 30;

/**
 * Dashboard widget (widget_kind = 'statement'): a template layout such as
 * the P&L or aged debtors. config: { datasetName, layoutKey, title?, period?,
 * budgetVersion?, inheritDateRange? (default true), refreshMinutes? }.
 */
const StatementWidget = ({ widget }) => {
  const config = widget.config || {};
  const datasetName = config.datasetName || widget.dataset_name;
  const { layoutKey } = config;
  const companyId = useStore((state) => state.currentCompany?.id);
  const dashboardDateRange = useDashboardDateRange();
  const wallMode = useStore((state) => state.wallMode);
  const navigate = useNavigate();
  const inherit = config.inheritDateRange !== false;
  // Period: the viewer's pick on this widget, else the dashboard period (when
  // it follows it), else the period saved on the widget, else the layout's default.
  const viewerPeriod = useStore((state) => state.widgetPeriods[widget.id]);
  const defaultPeriod = (inherit && dashboardDateRange) || config.period || null;
  const period = viewerPeriod || defaultPeriod;
  const refreshMinutes = config.refreshMinutes || DEFAULT_REFRESH_MINUTES;

  const [state, setState] = useState({ loading: true, error: null, result: null, at: null });
  // Only the newest request may update the widget: a period change while a
  // request is in flight must start a new one, not wait on (or drop) the old.
  const latest = useRef(0);

  useEffect(() => {
    if (!companyId || !datasetName || !layoutKey) return undefined;
    let cancelled = false;
    const load = async () => {
      const id = ++latest.current;
      try {
        const res = await statementsAPI.run(datasetName, layoutKey, companyId, { period, budgetVersion: config.budgetVersion });
        if (cancelled || id !== latest.current) return;
        if (!res?.success) throw new Error(res?.error || 'Statement failed');
        setState({ loading: false, error: null, result: res.data, at: res.data.generatedAt });
      } catch (err) {
        if (!cancelled && id === latest.current) setState((prev) => ({ ...prev, loading: false, error: err.response?.data?.error || err.message }));
      }
    };
    setState((prev) => ({ ...prev, loading: true, error: null }));
    load();
    const id = setInterval(load, refreshMinutes * 60 * 1000);
    return () => { cancelled = true; clearInterval(id); };
  }, [companyId, datasetName, layoutKey, period, config.budgetVersion, refreshMinutes]);

  const title = config.title || state.result?.layout?.title || 'Statement';
  const open = () => navigate(`/statements/${encodeURIComponent(datasetName)}/${encodeURIComponent(layoutKey)}${period ? `?period=${encodeURIComponent(period)}` : ''}`);

  return (
    <div className="widget report-widget statement-widget">
      <div className="widget-header">
        <span className="widget-title" title="Open statement" onClick={open} style={{ cursor: 'pointer' }}>{title}</span>
        <span className="report-header-tools">
          <FreshnessBadge kind="report" at={state.at} staleAfterMs={refreshMinutes * 3 * 60 * 1000} loading={state.loading} />
        </span>
      </div>
      <div className="widget-body report-body statement-body">
        {state.loading && !state.result ? <div className="report-status">Loading…</div>
          : state.error ? <div className="report-status report-status--error">⚠ {state.error}</div>
            : <StatementView result={state.result} compact={wallMode} maxColumns={wallMode ? 3 : null} />}
      </div>
      {state.result?.layout?.kind === 'statement' && (
        <WidgetPeriodPicker
          widgetId={widget.id}
          title={title}
          defaultPeriod={defaultPeriod || state.result.layout.defaultPeriod || null}
          appliedLabel={state.result.period?.label || null}
        />
      )}
    </div>
  );
};

export default StatementWidget;
